// Desprendible semanal INFORMATIVO. Nómina → Movimientos.
// Muestra el desglose ESTIMADO de la semana (nom_desprendible_semanal) y permite
// imprimirlo / guardarlo como PDF. NO retiene ni contabiliza nada: el pago semanal
// sigue siendo un anticipo; la liquidación definitiva es el rol mensual.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Printer, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/lib/hooks';
import { Campo, fieldCls, money, fecha, nomRpc, type AnticipoRow, type EmpleadoRow } from './shared';

interface Linea { concepto: string; monto: number }
interface Desprendible {
  empleado: string; cedula: string | null; codigo: string | null;
  periodo: string; anio: number; sueldo_mensual: number; dias: number;
  ingreso_semana: number;
  egresos: Linea[]; total_egresos: number; neto_estimado: number;
  recibido_anticipo: number; a_descontar_en_rol_mensual: number;
  provisiones: Linea[]; nota: string;
}

const esc = (v: unknown) => String(v ?? '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c] as string));
const m = (v: number | string | null | undefined) => money(v);

export function Desprendible() {
  const { user } = useAuth();
  const empresa = user?.empresa?.nombre ?? 'Empresa';
  const [empleadoId, setEmpleadoId] = useState('');
  const [periodo, setPeriodo] = useState('');

  const { data: empleados = [] } = useQuery<EmpleadoRow[]>({
    queryKey: ['nom-empleados-opt-act'],
    queryFn: () => nomRpc<EmpleadoRow[]>('nom_empleados', { _q: null, _solo_activos: true }),
  });
  const semanales = empleados.filter((e) => e.frecuencia_pago !== 'mensual');

  const { data: anticipos = [] } = useQuery<AnticipoRow[]>({
    queryKey: ['nom-antic-emp', empleadoId],
    queryFn: () => nomRpc<AnticipoRow[]>('nom_anticipos', { _empleado_id: empleadoId, _estado: null }),
    enabled: !!empleadoId,
  });
  const corridas = anticipos.filter((a) => a.origen === 'semana' || a.origen === 'quincena');

  const { data, isFetching } = useQuery<Desprendible>({
    queryKey: ['nom-desprendible', empleadoId, periodo],
    queryFn: () => nomRpc<Desprendible>('nom_desprendible_semanal', { _empleado: empleadoId, _periodo: periodo }),
    enabled: !!empleadoId && !!periodo,
  });

  const cambiarEmpleado = (id: string) => { setEmpleadoId(id); setPeriodo(''); };

  const imprimir = () => {
    if (!data) return;
    const egr = data.egresos.map((l) => `<tr><td>${esc(l.concepto)}</td><td class="r">${l.monto ? '−' : ''}${m(l.monto)}</td></tr>`).join('');
    const prov = data.provisiones.map((l) => `<tr><td>${esc(l.concepto)}</td><td class="r">${m(l.monto)}</td></tr>`).join('');
    const html = `<!doctype html><html lang="es"><head><meta charset="utf-8"><title>Desprendible ${esc(data.periodo)} — ${esc(data.empleado)}</title>
      <style>
        *{font-family:Arial,Helvetica,sans-serif;box-sizing:border-box}
        body{margin:0;padding:24px;color:#16211b;font-size:13px}
        .sheet{max-width:520px;margin:0 auto;border:1px solid #dfe4df;border-radius:12px;overflow:hidden}
        .hd{display:flex;justify-content:space-between;align-items:flex-start;gap:12px;padding:16px 18px;border-bottom:2px solid #16211b}
        .co{font-weight:800;font-size:15px;line-height:1.2}
        .co small{display:block;font-weight:600;color:#61726a;font-size:11px;margin-top:2px}
        .doc{text-align:right;font-size:11px;color:#61726a}
        .doc b{display:block;color:#16211b;font-size:12px;text-transform:uppercase}
        .who{padding:12px 18px;border-bottom:1px solid #e4e8e4;font-size:12.5px;display:grid;grid-template-columns:1fr 1fr;gap:2px 14px}
        .who b{color:#61726a;font-weight:600}
        .sect{padding:8px 18px 3px;font-size:11px;text-transform:uppercase;letter-spacing:.06em;font-weight:800;color:#1f7a4d}
        table{width:100%;border-collapse:collapse;font-size:13px}
        td{padding:3px 18px}
        td.r{text-align:right;font-variant-numeric:tabular-nums}
        tr.l td{border-bottom:1px solid #eef1ee}
        tr.t td{font-weight:800;border-top:1px solid #16211b}
        .neto{background:#eef4fb;margin:10px 18px;border-radius:10px;padding:10px 12px;display:flex;justify-content:space-between;align-items:center}
        .neto .k{font-size:11px;text-transform:uppercase;letter-spacing:.05em;color:#1d4ed8;font-weight:800}
        .neto .v{font-size:22px;font-weight:800;color:#1d4ed8}
        .rec{display:flex;justify-content:space-between;padding:5px 18px;font-size:12.5px}
        .prov td{color:#61726a}
        .nota{margin:8px 18px 16px;background:#fbf1dd;border:1px solid #e3c98a;border-radius:10px;padding:9px 11px;font-size:11px}
        .nota b{color:#8a5a00}
        @media print{@page{size:A5;margin:8mm}}
      </style></head><body>
      <div class="sheet">
        <div class="hd">
          <div class="co">${esc(empresa)}<small>Comprobante de pago semanal · informativo</small></div>
          <div class="doc"><b>${esc(data.periodo)}</b>${data.dias} días laborados</div>
        </div>
        <div class="who">
          <div><b>Trabajador:</b> ${esc(data.empleado)}</div>
          <div><b>Cédula:</b> ${esc(data.cedula ?? '—')}</div>
          <div><b>Código:</b> ${esc(data.codigo ?? '—')}</div>
          <div><b>Sueldo mensual:</b> ${m(data.sueldo_mensual)}</div>
        </div>
        <div class="sect">Ingreso de la semana</div>
        <table><tr class="l"><td>Jornal (${data.dias} días × sueldo/30)</td><td class="r" style="color:#059669;font-weight:700">${m(data.ingreso_semana)}</td></tr></table>
        <div class="sect">Egresos estimados (a cuenta)</div>
        <table>${egr}<tr class="t"><td>Total egresos estimados</td><td class="r" style="color:#dc2626">−${m(data.total_egresos)}</td></tr></table>
        <div class="neto"><span class="k">Neto estimado de la semana</span><span class="v">${m(data.neto_estimado)}</span></div>
        <div class="rec"><span><b>Recibido esta semana (anticipo a cuenta)</b></span><b>${m(data.recibido_anticipo)}</b></div>
        <div class="rec"><span>Se descontará en el rol del mes</span><span>${m(data.a_descontar_en_rol_mensual)}</span></div>
        <div class="prov"><div class="sect">Se te está acumulando (no es descuento)</div>
          <table>${prov}</table></div>
        <div class="nota"><b>Valores estimados a cuenta.</b> ${esc(data.nota)}</div>
      </div>
      <script>window.onload=function(){window.print();}</script></body></html>`;
    const win = window.open('', '_blank');
    if (!win) { return; }
    win.document.write(html); win.document.close();
  };

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold flex items-center gap-2"><FileText className="w-5 h-5" /> Desprendible semanal</h1>

      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Campo label="Empleado (semanal/quincenal)">
              <select className={fieldCls} value={empleadoId} onChange={(e) => cambiarEmpleado(e.target.value)}>
                <option value="">— Selecciona —</option>
                {semanales.map((e) => <option key={e.id} value={e.id}>{e.codigo ? `${e.codigo} · ` : ''}{e.razon_social}</option>)}
              </select>
            </Campo>
            <Campo label="Semana / corrida">
              <select className={fieldCls} value={periodo} onChange={(e) => setPeriodo(e.target.value)} disabled={!empleadoId}>
                <option value="">— Selecciona —</option>
                {corridas.map((a) => <option key={a.id} value={a.periodo ?? ''}>{a.periodo} · {fecha(a.fecha)} · {m(a.monto)}</option>)}
              </select>
            </Campo>
            <div className="flex items-end">
              <Button onClick={imprimir} disabled={!data}><Printer className="w-4 h-4 mr-1" /> Imprimir / PDF</Button>
            </div>
          </div>
          {empleadoId && corridas.length === 0 && (
            <p className="mt-3 text-sm text-muted-foreground">Este empleado no tiene corridas de anticipo semanales/quincenales. Genéralas en <b>Anticipos</b>.</p>
          )}
        </CardContent>
      </Card>

      {isFetching && <div className="py-8 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}

      {data && !isFetching && (
        <Card className="max-w-xl">
          <CardHeader><CardTitle className="text-base">{data.empleado} · {data.periodo} · {data.dias} días</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-[13px]">
              <div><span className="text-muted-foreground">Cédula:</span> {data.cedula ?? '—'}</div>
              <div><span className="text-muted-foreground">Código:</span> {data.codigo ?? '—'}</div>
              <div><span className="text-muted-foreground">Sueldo mensual:</span> {m(data.sueldo_mensual)}</div>
              <div><span className="text-muted-foreground">Ingreso de la semana:</span> <b className="text-emerald-600 dark:text-emerald-400">{m(data.ingreso_semana)}</b></div>
            </div>

            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-primary">Egresos estimados (a cuenta)</p>
              <table className="w-full">
                <tbody>
                  {data.egresos.map((l, i) => (
                    <tr key={i} className="border-b border-border/60"><td className="py-1">{l.concepto}</td><td className="py-1 text-right tabular-nums">{l.monto ? '−' : ''}{m(l.monto)}</td></tr>
                  ))}
                  <tr className="font-semibold"><td className="py-1">Total egresos estimados</td><td className="py-1 text-right tabular-nums text-urgente">−{m(data.total_egresos)}</td></tr>
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between rounded-md bg-blue-50 dark:bg-blue-950/40 px-3 py-2">
              <span className="text-xs font-bold uppercase tracking-wide text-blue-700 dark:text-blue-300">Neto estimado de la semana</span>
              <span className="text-xl font-bold tabular-nums text-blue-600 dark:text-blue-400">{m(data.neto_estimado)}</span>
            </div>

            <div className="text-[13px] space-y-0.5">
              <div className="flex justify-between"><span><b>Recibido esta semana</b> (anticipo a cuenta)</span><b>{m(data.recibido_anticipo)}</b></div>
              <div className="flex justify-between text-muted-foreground"><span>Se descontará en el rol del mes</span><span>{m(data.a_descontar_en_rol_mensual)}</span></div>
            </div>

            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Se le está acumulando (no es descuento)</p>
              <table className="w-full text-muted-foreground">
                <tbody>
                  {data.provisiones.map((l, i) => (
                    <tr key={i} className="border-b border-border/60"><td className="py-1">{l.concepto}</td><td className="py-1 text-right tabular-nums">{m(l.monto)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[12px] text-amber-900 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-200">
              <b>Valores estimados a cuenta.</b> {data.nota}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
